/**
 * @fileoverview Journey modal flow for the Period Calculator.
 * Handles the multi-step form wizard for collecting user cycle data.
 * @module period-tracker/period-journey
 */

/* global femcare, axios */

femcare.periodTracker = femcare.periodTracker || {};

/**
 * @namespace femcare.periodTracker.journey
 * @description Manages the period calculator journey modal flow.
 */
femcare.periodTracker.journey = {
    config: {
        totalSteps: 4,
        currentStep: 1,
        isInitialized: false,
        modalId: 'periodCalculatorJourneyModal',
        journeyCompleted: false
    },
    elements: {},
    onComplete: null,

    /**
     * Initializes the journey module.
     * @param {Object} config - Configuration
     * @param {Function} config.onComplete - Completion callback
     */
    init: function (config) {
        if (this.config.isInitialized) return;

        this.onComplete = config?.onComplete;
        this.cacheElements();
        this.bindEvents();
        this.config.isInitialized = true;
    },

    /**
     * Caches DOM elements.
     */
    cacheElements: function () {
        const modal = document.getElementById(this.config.modalId);
        if (!modal) return;

        this.elements = {
            modal,
            form: modal.querySelector('.period-calculator-journey'),
            steps: modal.querySelectorAll('.period-calculator-journey__step'),
            previousButton: modal.querySelector('[data-action="prev"]'),
            nextButton: modal.querySelector('[data-action="next"]'),
            submitButton: modal.querySelector('[type="submit"]'),
            currentStepSpan: modal.querySelector('.period-calculator-journey__current-step'),
            dateInput: modal.querySelector('.circular-date-picker__native-input input[type="date"]'),
            periodLengthInput: modal.querySelector('.period-calculator-journey__counter-input--period-length'),
            cycleLengthInput: modal.querySelector('.period-calculator-journey__counter-input--cycle-length'),
            cycleInsightsContainer: document.querySelector('.period-calculator__cycle-insights-container')
        };
    },

    /**
     * Binds event listeners.
     */
    bindEvents: function () {
        if (!this.elements.modal) return;

        const { modal, form, previousButton, nextButton } = this.elements;

        previousButton?.addEventListener('click', () => this.navigateStep(-1));
        nextButton?.addEventListener('click', () => this.handleNext());
        form?.addEventListener('submit', (event) => this.handleSubmit(event), true);

        form?.addEventListener('change', (event) => {
            if (event.target.matches('input[type="radio"], input[type="date"]')) {
                this.updateNavigationButtons();
            }
            if (event.target.matches('input[type="radio"]')) {
                this.updateRadioSelection(event.target);
            }
        });

        modal.addEventListener('click', (event) => {
            const button = event.target.closest('[data-action="increment"], [data-action="decrement"]');
            if (button) this.handleCounter(button);
        });

        document.addEventListener('click', (event) => {
            if (event.target.closest(`[data-modal-target="${this.config.modalId}"]`)) {
                this.setBackgroundFromData();
                this.resetJourney();
                this.initModalDatePicker();
            }
        });
    },

    /**
     * Sets modal background from data attribute.
     */
    setBackgroundFromData: function () {
        if (!this.elements.form || !this.elements.modal) return;

        const backgroundUrl = this.elements.form.dataset.background;
        if (backgroundUrl) {
            this.elements.modal.style.backgroundImage = `url('${backgroundUrl}')`;
        }
    },

    /**
     * Handles counter increment/decrement.
     * @param {HTMLElement} button - Counter button
     */
    handleCounter: function (button) {
        const targetId = button.dataset.target;
        const input = document.getElementById(targetId);
        if (!input) return;

        const action = button.dataset.action;
        const currentValue = parseInt(input.value);
        const min = parseInt(input.min);
        const max = parseInt(input.max);

        input.value = action === 'increment'
            ? Math.min(currentValue + 1, max)
            : Math.max(currentValue - 1, min);
    },

    /**
     * Navigates to a different step.
     * @param {number} direction - Step direction (-1 or 1)
     */
    navigateStep: function (direction) {
        const newStep = this.config.currentStep + direction;
        if (newStep >= 1 && newStep <= this.config.totalSteps) {
            this.config.currentStep = newStep;
            this.updateStepDisplay();
        }
    },

    /**
     * Handles next button click.
     */
    handleNext: function () {
        const currentStepElement = this.getCurrentStepElement();
        if (!currentStepElement) return;

        if (this.validateCurrentStep() && this.config.currentStep < this.config.totalSteps) {
            this.navigateStep(1);
        }
    },

    /**
     * Handles form submission.
     * @param {Event} event - Submit event
     */
    handleSubmit: function (event) {
        event.preventDefault();
        event.stopImmediatePropagation();

        if (this.validateCurrentStep()) {
            this.processJourneyResults();
        }
    },

    /**
     * Validates current step inputs.
     * @returns {boolean} True if valid
     */
    validateCurrentStep: function () {
        const currentStepElement = this.getCurrentStepElement();
        if (!currentStepElement) return false;

        const inputs = currentStepElement.querySelectorAll('[data-validate]');
        let isValid = true;

        inputs.forEach(input => {
            const $input = $(input);
            $input.validate();
            if (!$input[0].validity.valid) {
                isValid = false;
            }
        });

        return isValid;
    },

    /**
     * Gets current step element.
     * @returns {HTMLElement|null} Current step element
     */
    getCurrentStepElement: function () {
        return this.elements.modal?.querySelector(`[data-step="${this.config.currentStep}"]`);
    },

    /**
     * Updates step display and navigation.
     */
    updateStepDisplay: function () {
        if (!this.elements.modal) return;

        this.elements.steps.forEach(step => step.classList.remove('displayed'));

        const currentStepElement = this.getCurrentStepElement();
        currentStepElement?.classList.add('displayed');

        if (this.elements.currentStepSpan) {
            this.elements.currentStepSpan.textContent = this.config.currentStep;
        }

        this.updateNavigationButtons();
    },

    /**
     * Updates navigation button visibility.
     */
    updateNavigationButtons: function () {
        const { previousButton, nextButton, submitButton } = this.elements;
        const isValid = this.isCurrentStepValid();
        const isLastStep = this.config.currentStep === this.config.totalSteps;

        if (previousButton) {
            previousButton.classList.toggle('hidden', this.config.currentStep === 1);
            previousButton.classList.toggle('displayed', this.config.currentStep > 1);
        }

        if (nextButton) {
            nextButton.classList.toggle('hidden', isLastStep);
            nextButton.classList.toggle('displayed', !isLastStep);
            nextButton.disabled = !isValid;
        }

        if (submitButton) {
            submitButton.classList.toggle('hidden', !isLastStep);
            submitButton.classList.toggle('displayed', isLastStep);
            submitButton.disabled = !isValid;
        }
    },

    /**
     * Checks if current step has valid inputs.
     * @returns {boolean} True if valid
     */
    isCurrentStepValid: function () {
        const currentStepElement = this.getCurrentStepElement();
        if (!currentStepElement) return false;

        const inputs = currentStepElement.querySelectorAll('[data-validate]');
        if (inputs.length === 0) return true;

        return Array.from(inputs).every(input => {
            if (!input.hasAttribute('required')) return true;

            if (input.type === 'radio') {
                const radioGroup = currentStepElement.querySelectorAll(`input[name="${input.name}"]`);
                return Array.from(radioGroup).some(radio => radio.checked);
            }

            return !!input.value;
        });
    },

    /**
     * Updates radio button selection styling.
     * @param {HTMLInputElement} radioInput - Selected radio
     */
    updateRadioSelection: function (radioInput) {
        const radioName = radioInput.name;
        const radioGroup = this.elements.form.querySelectorAll(`input[name="${radioName}"]`);

        radioGroup.forEach(radio => {
            const formRadioControl = radio.closest('.form-radio-control');
            if (formRadioControl) {
                formRadioControl.classList.toggle('period-calculator-journey__radio-chosen', radio.checked);
            }
        });
    },

    /**
     * Processes journey results.
     */
    processJourneyResults: async function () {
        femcare.tracking.forms.formSubmit(false, $(this.elements.form));

        this.showResults();
        this.config.journeyCompleted = true;
        this.closeModal();

        const journeyData = this.getJourneyData();

        if (this.onComplete) {
            this.onComplete(journeyData);
        }

        await this.fetchCycleInsights();
        this.scrollToCycleView();
    },

    /**
     * Gets journey form data.
     * @returns {Object} Journey data
     */
    getJourneyData: function () {
        const dateValue = this.elements.dateInput?.value;
        let lastPeriodDate = null;

        if (dateValue) {
            const dateParts = dateValue.split('-');
            if (dateParts.length === 3) {
                lastPeriodDate = new Date(
                    parseInt(dateParts[0], 10),
                    parseInt(dateParts[1], 10) - 1,
                    parseInt(dateParts[2], 10),
                    12, 0, 0, 0
                );
            }
        }

        return {
            periodLength: parseInt(this.elements.periodLengthInput?.value, 10) || 6,
            cycleLength: parseInt(this.elements.cycleLengthInput?.value, 10) || 28,
            lastPeriodDate: lastPeriodDate
        };
    },

    /**
     * Closes the modal.
     */
    closeModal: function () {
        if (this.elements.modal && femcare.modal) {
            femcare.modal.closeModal(this.elements.modal);
        }
    },

    /**
     * Resets journey to initial state.
     */
    resetJourney: function () {
        this.config.currentStep = 1;

        if (this.elements.form) {
            this.elements.form.reset();

            if (this.elements.periodLengthInput) this.elements.periodLengthInput.value = 6;
            if (this.elements.cycleLengthInput) this.elements.cycleLengthInput.value = 28;

            const errorMessages = this.elements.form.querySelectorAll('.validation-error span');
            errorMessages.forEach(span => { span.textContent = ''; });

            const radioControls = this.elements.form.querySelectorAll('.form-radio-control');
            radioControls.forEach(control => {
                control.classList.remove('period-calculator-journey__radio-chosen');
            });
        }

        this.updateStepDisplay();
    },

    /**
     * Initializes modal date picker.
     */
    initModalDatePicker: function () {
        const modalDatePicker = this.elements.modal?.querySelector('.circular-date-picker');
        if (!modalDatePicker) return;

        femcare.form.circularDatePicker?.init(modalDatePicker);
    },

    /**
     * Shows results section.
     */
    showResults: function () {
        const landingSection = document.querySelector('.period-calculator__landing');
        const resultsSection = document.querySelector('.period-calculator__results');

        landingSection?.classList.add('hidden');
        resultsSection?.classList.remove('hidden');
    },

    /**
     * Fetches cycle insights from backend.
     */
    fetchCycleInsights: async function () {
        try {
            femcare.loader?.toggleLoader(this.elements.cycleInsightsContainer, true);

            const token = this.elements.form.getAttribute('data-token');
            const formData = new FormData(this.elements.form);

            const response = await axios({
                method: 'POST',
                url: this.elements.form.getAttribute('action'),
                data: formData,
                headers: {
                    'X-Request-Verification-Token': token,
                    'X-Requested-With': 'XMLHttpRequest'
                },
                validateStatus: () => true
            });

            const data = response.data;

            if (data?.success && data.validationPassed && data.content) {
                this.replaceCycleInsights(data.content);
                this.reinitializeCycleInsightsJS();

                const initialPhase = data.extra?.initialPhase;
                const periodStatementContent = data.extra?.periodStatementContent;

                if (periodStatementContent) {
                    this.insertPeriodStatement(periodStatementContent, initialPhase);
                }

                const predictionWarning = data.extra?.predictionWarning;
                if (predictionWarning) {
                    this.updatePredictionWarning(predictionWarning);
                }

                this.setCalendarStartingDate(initialPhase);
            } else if (data?.error) {
                femcare.form.protection?.handleError({ response: { data } });
                this.setCalendarStartingDate();
            }
        } catch (error) {
            femcare.form.protection?.handleError(error);
            this.setCalendarStartingDate();
        } finally {
            femcare.loader?.toggleLoader(this.elements.cycleInsightsContainer, false);
        }
    },

    /**
     * Replaces cycle insights HTML.
     * @param {string} newHtml - New HTML content
     */
    replaceCycleInsights: function (newHtml) {
        const cycleInsights = this.elements.cycleInsightsContainer?.querySelector('.cycle-insights');
        if (cycleInsights) {
            cycleInsights.outerHTML = newHtml;
        }
    },

    /**
     * Reinitializes cycle insights after HTML replacement.
     */
    reinitializeCycleInsightsJS: function () {
        femcare.periodTracker.insights?.refresh();

        const selectedDate = femcare.periodTracker.wheel?.getCurrentDay
            ? new Date(
                femcare.periodTracker.navigation?.currentMonth.getFullYear(),
                femcare.periodTracker.navigation?.currentMonth.getMonth(),
                femcare.periodTracker.wheel.getCurrentDay(),
                12, 0, 0, 0
            )
            : new Date();

        femcare.periodTracker.insights?.update(selectedDate);
    },

    /**
     * Sets calendar starting date based on phase.
     * @param {string} [initialPhase] - Initial phase preference
     */
    setCalendarStartingDate: function (initialPhase) {
        const cycleLogic = femcare.periodTracker.cycleLogic;
        const navigation = femcare.periodTracker.navigation;
        const wheel = femcare.periodTracker.wheel;

        if (!cycleLogic || !navigation || !wheel) return;

        const today = new Date();
        today.setHours(12, 0, 0, 0);

        let targetDate = today;

        if (initialPhase === 'next-menstruation') {
            targetDate = cycleLogic.calculateNextPeriodDate(today) || today;
        } else if (initialPhase === 'next-ovulation') {
            targetDate = cycleLogic.calculateNextOvulationDate(today) || today;
        }

        navigation.setCurrentMonth(targetDate);
        wheel.setCurrentDay(targetDate.getDate(), false);
    },

    /**
     * Inserts period statement HTML.
     * @param {string} periodStatementHtml - Statement HTML
     * @param {string} initialPhase - Phase type
     */
    insertPeriodStatement: function (periodStatementHtml, initialPhase) {
        const periodCalendarWrapper = document.querySelector('.period-calendar__wrapper');
        if (!periodCalendarWrapper) return;

        const existingStatement = document.querySelector('.period-statement');
        existingStatement?.remove();

        periodCalendarWrapper.insertAdjacentHTML('beforebegin', periodStatementHtml);

        const periodStatementDate = document.querySelector('.period-statement__date');
        if (!periodStatementDate) return;

        const cycleLogic = femcare.periodTracker.cycleLogic;
        const today = new Date();
        today.setHours(12, 0, 0, 0);

        let targetDate = null;

        if (initialPhase === 'next-menstruation') {
            targetDate = cycleLogic.calculateNextPeriodDate(today);
        } else if (initialPhase === 'next-ovulation') {
            targetDate = cycleLogic.calculateNextOvulationDate(today);
        }

        if (targetDate) {
            periodStatementDate.textContent = this.formatDate(targetDate);
        }
    },

    /**
     * Updates prediction warning HTML content.
     * @param {string} warningHtml - Warning HTML content
     */
    updatePredictionWarning: function (warningHtml) {
        const warning = femcare.periodTracker.warning;
        if (warning) {
            warning.replaceWarningContent(warningHtml);
        }
    },

    /**
     * Formats date for display.
     * @param {Date} date - Date to format
     * @returns {string} Formatted date
     */
    formatDate: function (date) {
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('en-GB', options);
    },

    /**
     * Scrolls to cycle view after results load.
     */
    scrollToCycleView: function () {
        const wrapperElement = document.querySelector('.period-calendar__wrapper');
        if (!wrapperElement) return;

        const timing = femcare.periodTracker.config.timing;

        setTimeout(() => {
            const navHeight = document.querySelector('.navigation')?.offsetHeight || 0;
            const wrapperTop = wrapperElement.getBoundingClientRect().top + window.scrollY;

            window.scrollTo({
                top: wrapperTop - navHeight,
                behavior: 'smooth'
            });

            setTimeout(() => {
                femcare.periodTracker.tooltip?.showIfFirstTime();
            }, timing.tooltipAfterScroll);
        }, timing.scrollDelay);
    }
};
